import AssetService from '../Services/assets.service';
import {
    mapSavedCapitalGainEnhancementToChild,
    mapSavedReplacementsToReplacementData,
} from './capitalGainCalculations';

export const EMPTY_CAPITAL_GAIN_REPLACEMENTS = {
    selectedAssets: [],
    realReplacementAmount: 0,
    inflationaryReplacementAmount: 0,
};

/**
 * Loads every input needed by buildCapitalGainResult for one sold asset:
 * the basis at sale, the index calculation, saved enhancements and saved replacements.
 * The index and the optional parts never throw; only a missing basis does.
 *
 * @returns {Promise<{ basis, cpi, cpiError, enhancements, savedEnhancements, replacements }>}
 */
export async function loadCapitalGainInputs({ caseId, assetId, token, fallbackAsset = null }) {
    const basisResp = await AssetService.GetCapitalGainDepreciationAtSale(caseId, assetId, token);
    if (!basisResp?.success || !basisResp.data) {
        throw new Error(basisResp?.error || 'FailedToLoadCapitalGainData');
    }
    const basis = basisResp.data;

    const saleDate = basis.sellDate || fallbackAsset?.sellDate || fallbackAsset?.SellDate;
    const purchaseDate =
        basis.purchaseDate || fallbackAsset?.purchaseDate || fallbackAsset?.PurchaseDate;
    const reductionContext = {
        saleDate,
        parentDepreciationPercentage:
            basis.parentDepreciationPercentage ??
            basis.ParentDepreciationPercentage ??
            fallbackAsset?.depreciationPercentage ??
            fallbackAsset?.DepreciationPercentage ??
            0,
    };

    const [replResult, enhResult, cpiResult] = await Promise.allSettled([
        AssetService.GetCapitalGainReplacements(caseId, assetId, token),
        AssetService.GetCapitalGainEnhancements(caseId, assetId, token),
        AssetService.GetCapitalGainIndexCalculation(purchaseDate, saleDate, token),
    ]);

    const replResp = replResult.status === 'fulfilled' ? replResult.value : null;
    const replacements =
        replResp?.success && Array.isArray(replResp.data) && replResp.data.length > 0
            ? mapSavedReplacementsToReplacementData(replResp.data, reductionContext)
            : EMPTY_CAPITAL_GAIN_REPLACEMENTS;

    const enhResp = enhResult.status === 'fulfilled' ? enhResult.value : null;
    const savedEnhancements =
        enhResp?.success && Array.isArray(enhResp.data) ? enhResp.data : [];

    let cpi = null;
    let cpiError = null;
    if (cpiResult.status === 'fulfilled' && cpiResult.value?.success) {
        cpi = cpiResult.value.data;
    } else {
        cpiError =
            readableCapitalGainError(
                cpiResult.status === 'fulfilled' ? cpiResult.value?.error : cpiResult.reason
            ) || 'FailedToLoadCpiData';
    }

    return {
        basis: { ...basis, sellDate: saleDate, purchaseDate },
        cpi,
        cpiError,
        enhancements: savedEnhancements.map((row) => mapSavedCapitalGainEnhancementToChild(row)),
        savedEnhancements,
        replacements,
    };
}

/** English server texts mapped to translation keys. */
const CAPITAL_GAIN_SERVER_MESSAGES = {
    'Sale date must be on or after purchase date.': 'SaleDateBeforePurchaseDate',
};

/** A translation key when the server text is known, otherwise the text itself. */
export function capitalGainErrorKey(message) {
    const text = String(message || '').trim();
    return CAPITAL_GAIN_SERVER_MESSAGES[text] || text;
}

/** The server message inside an error thrown with the raw response body ({ success, error }). */
export function readableCapitalGainError(error) {
    const message = error?.message || String(error || '');
    try {
        const parsed = JSON.parse(message);
        return capitalGainErrorKey(parsed?.error || parsed?.message || parsed?.title || message);
    } catch {
        return capitalGainErrorKey(message);
    }
}

/** Runs async tasks with at most `limit` in flight. */
export async function runWithConcurrency(items, limit, task) {
    const results = new Array(items.length);
    let next = 0;
    const worker = async () => {
        while (next < items.length) {
            const index = next;
            next += 1;
            try {
                results[index] = { status: 'fulfilled', value: await task(items[index], index) };
            } catch (error) {
                results[index] = { status: 'rejected', reason: error };
            }
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return results;
}
